import React, { useEffect, useRef } from 'react'
import { appLogout } from '../services/logoutService'
import { GetUserName } from '../services/AuthUser'
import { environmentData } from '../environments/env'

// Monitors user inactivity globally (all routes) and triggers logout after timeout.
// Timeout can be overridden via REACT_APP_IDLE_TIMEOUT_MS env (milliseconds).
// Falls back to 10s for dev/testing if not specified.
const InactivityMonitor = () => {
  const runEnv = environmentData.run
  const userDetails = GetUserName()
  const adminEmail = environmentData[runEnv]?.dashboardAdminEmail
  const userEmail = userDetails?.[0]?.userEmail
  const baseTimeout = process.env.REACT_APP_IDLE_TIMEOUT_MS
    ? parseInt(process.env.REACT_APP_IDLE_TIMEOUT_MS, 10)
    : 20 * 60 * 1000 // default 20m (adjust in .env for production)
  // Allow forcing normal timeout even for admin (handy when testing with admin account)
  const forceAdminTimeout = process.env.REACT_APP_FORCE_ADMIN_TIMEOUT === 'true'
  const timeoutMs = !forceAdminTimeout && userEmail === adminEmail ? 2592000000 : baseTimeout
  const debugIdle = process.env.REACT_APP_IDLE_DEBUG === 'true'
  const lastActivityRef = useRef(Date.now())
  const intervalRef = useRef(null)

  useEffect(() => {
    // Seed existing stored timestamp for consistency across reloads
    const stored = sessionStorage.getItem('lastActivity')
    if (stored) {
      lastActivityRef.current = parseInt(stored, 10) || Date.now()
    } else {
      sessionStorage.setItem('lastActivity', lastActivityRef.current.toString())
    }

    const activityHandler = (e) => {
      // Ignore synthetic/programmatic events (e.isTrusted === false) so programmatic scrolls or triggers
      // don't continually reset the idle timer on pages with auto UI updates.
      if (e && e.isTrusted === false) return
      const now = Date.now()
      lastActivityRef.current = now
      sessionStorage.setItem('lastActivity', now.toString())
      localStorage.setItem('lastActivityBroadcast', now.toString()) // cross-tab signal
      if (debugIdle) {
        // eslint-disable-next-line no-console
        console.log('[IdleDebug] Activity event:', e?.type, 'timestamp:', now)
      }
    }

    const storageHandler = (e) => {
      if (e.key === 'lastActivityBroadcast' && e.newValue) {
        lastActivityRef.current = parseInt(e.newValue, 10)
        sessionStorage.setItem('lastActivity', lastActivityRef.current.toString())
      }
    }

    const events = ['click', 'keypress', 'mousemove', 'scroll', 'touchstart']
    events.forEach((evt) => window.addEventListener(evt, activityHandler))
    window.addEventListener('storage', storageHandler)

    intervalRef.current = setInterval(() => {
      const now = Date.now()
      const idleFor = now - lastActivityRef.current
      if (debugIdle && idleFor % 5000 < 1000) {
        // log roughly every 5s without spamming
        // eslint-disable-next-line no-console
        console.log('[IdleDebug] Idle for ms:', idleFor, 'timeoutMs:', timeoutMs)
      }
      if (idleFor >= timeoutMs) {
        if (debugIdle) {
          // eslint-disable-next-line no-console
          console.log('[IdleDebug] Idle threshold reached. Logging out.')
        }
        clearInterval(intervalRef.current)
        appLogout()
      }
    }, 1000)

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, activityHandler))
      window.removeEventListener('storage', storageHandler)
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [timeoutMs, adminEmail, userEmail, debugIdle])

  return null
}

export default InactivityMonitor
